
import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, MoveDown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface DismissalData {
  dismissed: boolean;
  timestamp: number;
  count: number;
}

export function PwaInstallBanner() {
  const { isInstallable, isIOS, promptToInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  useEffect(() => {
    const checkDismissalStatus = () => {
      const rawData = localStorage.getItem('pwa-install-dismissed');
      if (!rawData) {
        return false;
      }
      
      let dismissalData: DismissalData;
      try {
        dismissalData = JSON.parse(rawData);
      } catch (e) {
        return rawData === 'true';
      }
      
      const weekInMs = 7 * 24 * 60 * 60 * 1000;
      const timeSinceDismissal = Date.now() - dismissalData.timestamp;
      
      if (dismissalData.count >= 3) {
        return true;
      }
      
      return dismissalData.dismissed && timeSinceDismissal <= weekInMs;
    };
    
    setDismissed(checkDismissalStatus());
  }, []);
  
  // If user is not logged in, or the banner should be dismissed, or it's not installable, don't show it
  if (!user || !isInstallable || dismissed) {
    return null;
  }
  
  const handleDismiss = () => {
    setDismissed(true);
    
    const rawData = localStorage.getItem('pwa-install-dismissed');
    let dismissalData: DismissalData;
    
    if (!rawData) {
      dismissalData = { dismissed: true, timestamp: Date.now(), count: 1 };
    } else {
      try {
        dismissalData = JSON.parse(rawData);
        dismissalData.dismissed = true;
        dismissalData.timestamp = Date.now();
        dismissalData.count += 1;
      } catch (e) {
        dismissalData = { dismissed: true, timestamp: Date.now(), count: 1 };
      }
    }
    
    localStorage.setItem('pwa-install-dismissed', JSON.stringify(dismissalData));
    
    toast({
      title: t('pwa.dismissed', 'Installation dismissed'),
      description: t('pwa.dismissedMessage', 'You can install the app later from the settings page'),
      duration: 3000
    });
  };
  
  const handleInstallClick = () => {
    if (isIOS) {
      setShowIosInstructions(true);
    } else {
      promptToInstall().catch(error => {
        console.error('Install error:', error);
        toast({
          title: t('pwa.installError', 'Installation error'),
          description: t('pwa.tryAgain', 'Please try again later'),
          variant: 'destructive',
          duration: 3000
        });
      });
    }
  };
  
  const bannerContent = (
    <div className="fixed inset-x-0 bottom-0 z-50 bg-primary text-white p-4 flex items-center justify-between shadow-lg rounded-t-xl">
      <div className="flex items-center">
        <div className="mr-3">
          <Smartphone className="h-6 w-6" />
        </div>
        <div>
          <h3 className="font-medium text-lg">Install mishu</h3>
          <p className="text-sm opacity-90">Get instant notifications, faster access & offline features</p>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <Button 
          variant="outline" 
          className="border border-white text-white hover:bg-primary/90 bg-transparent rounded-full"
          onClick={handleInstallClick}
        >
          <MoveDown className="mr-2 h-4 w-4" />
          {t('pwa.install', 'Install')}
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-white hover:bg-primary/90 bg-transparent rounded-full"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
  
  const iOSInstructionsContent = (
    <>
      {isMobile ? (
        <Sheet open={showIosInstructions} onOpenChange={setShowIosInstructions}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{t('pwa.iosInstallTitle', 'Install on your iOS device')}</SheetTitle>
            </SheetHeader>
            <div className="py-4 space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <MoveDown className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction1', 'Tap the Share button')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction1Detail', 'Look for the share icon in Safari')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction2', 'Tap "Add to Home Screen"')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction2Detail', 'Scroll down in the share menu if needed')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction3', 'Tap "Add" in the top right')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction3Detail', 'The app will be installed on your home screen for instant access')}</p>
                </div>
              </div>
            </div>
            <SheetFooter>
              <Button 
                onClick={() => setShowIosInstructions(false)}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {t('actions.gotIt', 'Got it')}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={showIosInstructions} onOpenChange={setShowIosInstructions}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('pwa.iosInstallTitle', 'Install on your iOS device')}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <MoveDown className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction1', 'Tap the Share button')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction1Detail', 'Look for the share icon in Safari')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction2', 'Tap "Add to Home Screen"')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction2Detail', 'Scroll down in the share menu if needed')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction3', 'Tap "Add" in the top right')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction3Detail', 'The app will be installed on your home screen for instant access')}</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button 
                onClick={() => setShowIosInstructions(false)}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {t('actions.gotIt', 'Got it')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
  
  return (
    <>
      {bannerContent}
      {iOSInstructionsContent}
    </>
  );
}
